/* ============================================================================
 * VentureSection, direct structural port of
 * growthjockey--website/app/components/sections/ventureSection.tsx
 * (data sourced from getAllVentureCards() in that codebase's server.tsx).
 *
 * Left column: eyebrow + dual-tone heading ("We Design. We Build. We Scale.")
 * + body prose + CTA + auxiliary illustration. Right column: 6 metric cards
 * distributed across 3 desktop columns / single mobile column.
 *
 * Differences from GJ source:
 *  - No submodule/ItemBuilder deps; inline `.filter().map()` instead.
 *  - No `useIsDesktop` hook; pure CSS responsive (mobile = stack, lg = 3 cols).
 *  - No sibling hover-translate stencil; simple per-card lift on hover.
 *  - Gradient via our `text-gradient-blue` utility (same gradient as GJ's
 *    `<GradientText>`).
 *  - CTA points to /contact (the closest equivalent to /start-a-project).
 * ============================================================================ */

import {CtaButtonFilled} from "~/components/ctaButtonFilled";
import {useFrontendProxyUrl} from "~/root/rootLoaderData";

interface VentureCardData {
    tag: string;
    title: string;
    text: string;
    src: string;
    href: string;
    desktopColumn: 0 | 1 | 2;
}

const VENTURE_CARDS: ReadonlyArray<VentureCardData> = [
    {
        tag: "CONSUMER PRODUCTS",
        title: "45%",
        text: "Reduction in customer acquisition cost",
        src: "https://growthjockey-prod.b-cdn.net/new/VentureCard1-v2.webp",
        href: "https://www.growthjockey.com/",
        desktopColumn: 0,
    },
    {
        tag: "FINTECH",
        title: "4X",
        text: "Increase in digital service adoption through AI",
        src: "https://growthjockey-prod.b-cdn.net/new/VentureCard2-v2.webp",
        href: "https://www.growthjockey.com/industries/banking-and-fintech",
        desktopColumn: 1,
    },
    {
        tag: "AUTOMOBILE",
        title: "3.5X",
        text: "Growth in end to end user engagement",
        src: "https://growthjockey-prod.b-cdn.net/new/VentureCard3-v2.webp",
        href: "https://www.growthjockey.com/industries/ev-and-automobile",
        desktopColumn: 1,
    },
    {
        tag: "DIGITAL COMMERCE",
        title: "60%",
        text: "Increase in lead to sales conversion across channels",
        src: "https://growthjockey-prod.b-cdn.net/new/VentureCard4-v2.webp",
        href: "https://www.growthjockey.com/industries/ecommerce",
        desktopColumn: 2,
    },
    {
        tag: "HEALTHCARE",
        title: "5X",
        text: "Increase in quality admissions and CAC efficiency",
        src: "https://growthjockey-prod.b-cdn.net/new/VentureCard5-v2.webp",
        href: "https://www.growthjockey.com/",
        desktopColumn: 2,
    },
];

function VentureCard({tag, title, text, src, href}: VentureCardData) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="relative block w-full h-[14rem] lg:h-[23.75rem] rounded-lg overflow-hidden no-underline transition-transform duration-[300ms] hover:-translate-y-1"
        >
            <img
                src={src}
                alt=""
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/30 pointer-events-none" />
            <div className="relative h-full p-3 lg:px-5 lg:py-lg grid grid-rows-[auto_1fr_auto] text-[#FFFFFF]">
                <h4 className="text-nav-text-b lg:text-medium-b">{tag}</h4>
                <div className="text-heading2-25 self-center">{title}</div>
                <div className="text-nav-text lg:text-body line-clamp-2 lg:line-clamp-3">{text}</div>
            </div>
        </a>
    );
}

export function VentureSection() {
    const continueToIntellsysUrl = useFrontendProxyUrl("/u");

    return (
        <section
            aria-labelledby="venture-title"
            className="gj-px-screen-edge-constrained gj-py-section-divider grid grid-cols-1 lg:grid-cols-[40%_60%] gap-md lg:gap-md3 items-start"
        >
            {/* Left column */}
            <div className="grid grid-cols-1 gap-lg">
                <h2
                    id="venture-title"
                    className="text-heading2-25 text-text-primary"
                >
                    We<span className="text-gradient-blue"> Connect.</span> We<span className="text-gradient-blue"> Automate.</span> We
                    <span className="text-gradient-blue"> Scale.</span>
                </h2>
                <p className="text-body text-text-body">
                    Intellsys OS replaces 30+ disconnected tools with one operating system, 24 apps, AI agents and a unified data layer that runs every team in
                    your venture.
                    <br />
                    <br />
                    From the first lead to the hundredth dealer, every workflow lives in one place. Connected by data, automated by agents, run by your people.
                </p>
                <div className="justify-self-start">
                    <CtaButtonFilled
                        text="Continue to Intellsys"
                        href={continueToIntellsysUrl}
                        arrow
                    />
                </div>
            </div>

            {/* Right column, single column on mobile, 3 columns on desktop. */}
            {/* Desktop: 3-column track, cards distributed by `desktopColumn`. */}
            <div className="hidden lg:grid grid-cols-3 gap-md items-start">
                {[0, 1, 2].map((col) => (
                    <div
                        key={col}
                        className="grid grid-cols-1 gap-md"
                    >
                        {VENTURE_CARDS.filter((c) => c.desktopColumn === col).map((card) => (
                            <VentureCard
                                key={card.tag}
                                {...card}
                            />
                        ))}
                    </div>
                ))}
            </div>
            {/* Mobile: 2 cards per row. If count is odd, the last card spans both columns
             * and is centered with the same width as a normal column (50% − half gap). */}
            <div className="grid grid-cols-2 gap-sm lg:hidden">
                {VENTURE_CARDS.map((card, idx) => {
                    const isOrphan = idx === VENTURE_CARDS.length - 1 && VENTURE_CARDS.length % 2 === 1;
                    if (isOrphan) {
                        return (
                            <div
                                key={card.tag}
                                className="col-span-2 mx-auto w-[calc(50%-0.375rem)]"
                            >
                                <VentureCard {...card} />
                            </div>
                        );
                    }
                    return (
                        <VentureCard
                            key={card.tag}
                            {...card}
                        />
                    );
                })}
            </div>
        </section>
    );
}
